import type { DataElement, DicomJsonDataset, NaturalizedDataset } from 'dcmjs';
import { DicomDataset, DicomElement } from './dataset';
import { keywordForTag, rawDictionaryVrForTag, vrForTag } from './dictionary';
import { DicomFile } from './reader';
import {
    FILE_META_GROUP_LENGTH_TAG,
    SPECIFIC_CHARACTER_SET_TAG,
    Tag,
    formatTag,
} from './tag';
import { Vr, isStringVr } from './vr';

/**
 * Projection of the engine's model into the shape dcmjs's
 * `naturalizeDataset` produces.
 *
 * This is the strangler shim: it lets the engine's reader stand in for
 * dcmjs's behind `DicomProcessor.readDicomDataset` without any of the ~40
 * files downstream of that seam changing, so the swap can be verified as a
 * no-op before anything migrates. It is deliberately temporary — each call
 * site that moves to the typed model is one less thing this has to imitate,
 * and it is deleted when the last one has.
 *
 * Fidelity is therefore the goal, including the quirks. The shape is
 * reproduced as dcmjs builds it: values unwrapped from their arrays when
 * singular, sequences and person names left as one-element arrays, `_vrMap`
 * recording representations that differ from the dictionary, and `_meta` as
 * keyword-keyed data elements.
 *
 * That includes dcmjs's `addAccessors` Proxy over a one-element value, which
 * forwards unknown property reads to element zero. The forwarding is load
 * bearing: `dcmjsToStructureSet` reaches through it three levels deep, as
 * `ReferencedFrameOfReferenceSequence.RTReferencedStudySequence
 * .RTReferencedSeriesSequence.SeriesInstanceUID`, and every structure set in
 * the application fails to load without it.
 *
 * One behaviour of that Proxy is not reproduced: dcmjs's `set` trap tests
 * `typeof prop === 'number'`, which never holds because a proxied property
 * key is always a string, so an `Array.push` writes its new element onto
 * element zero as a property named "1" and leaves the array sparse. That is
 * the corruption a caller avoids by copying the array out before appending.
 * Here a numeric key is recognized as one and writes to the array, so the
 * same push appends. Nothing depends on the corrupting behaviour; the
 * difference can only turn a silent corruption into the intended write.
 */

/** Builds the naturalized form of a parsed file, `_meta` included. */
export function toNaturalizedDataset(file: DicomFile): NaturalizedDataset {
    const natural = naturalizeDataset(file.dataset);
    natural._meta = toFileMeta(file.meta);
    return natural;
}

/** Builds the naturalized form of one dataset, without `_meta`. */
export function naturalizeDataset(dataset: DicomDataset): NaturalizedDataset {
    const vrMap: Record<string, string> = {};
    const natural: NaturalizedDataset = { _vrMap: vrMap };

    for (const element of dataset.orderedElements()) {
        const name = naturalNameFor(element.tag);
        recordVrMap(vrMap, name, element);
        const values =
            naturalizeSpecificCharacterSet(element) ?? naturalizeValue(element);
        // dcmjs represents an attribute that carries no value at all as null,
        // distinguishing it from one whose value is an empty string.
        natural[name] = values === undefined ? null : unwrapSingleValue(values);
    }
    return natural;
}

/**
 * The key an attribute takes in a naturalized dataset: its dictionary
 * keyword, or its unpunctuated tag when the dictionary has no entry.
 */
function naturalNameFor(tag: Tag): string {
    return keywordForTag(tag) ?? formatTag(tag);
}

/**
 * Records an attribute whose representation differs from the dictionary's.
 *
 * dcmjs's writer consults `_vrMap` to restore the original VR, and the
 * anonymizer reads it directly for PixelData and RescaleIntercept — the two
 * attributes whose representation is data-dependent — so it has to be present
 * and correct, not merely present.
 */
function recordVrMap(
    vrMap: Record<string, string>,
    name: string,
    element: DicomElement
): void {
    const rawVr = rawDictionaryVrForTag(element.tag);
    if (rawVr === undefined) {
        return;
    }
    if (rawVr === 'ox' || rawVr !== element.vr) {
        vrMap[name] = element.vr;
    }
}

/**
 * Applies dcmjs's single-value rule.
 *
 * A one-element value collapses to that element unless the element is a
 * non-array object — a sequence item, a person name, or a pixel buffer —
 * which stays wrapped. That asymmetry is why a single-item sequence needs an
 * `Array.isArray` branch at every call site downstream, and why removing the
 * rule is the point of the typed model rather than of this shim.
 */
function unwrapSingleValue(values: unknown[]): unknown {
    if (values.length !== 1) {
        return values;
    }
    const only = values[0];
    if (
        only !== null &&
        typeof only === 'object' &&
        !(only as { length?: unknown }).length
    ) {
        return addAccessors(values);
    }
    return only;
}

/** True for a property key that addresses an array element, such as "0". */
function isArrayIndex(prop: string): boolean {
    return /^(?:0|[1-9]\d*)$/.test(prop);
}

/**
 * Wraps a one-element value so that reads and writes of properties it does
 * not have are forwarded to that element, matching dcmjs's `addAccessors`.
 *
 * This is what lets `sequence.Attribute` mean `sequence[0].Attribute` for a
 * single-item sequence, which callers downstream of this shim rely on.
 */
function addAccessors(values: unknown[]): unknown[] {
    return new Proxy(values, {
        get(target, prop) {
            if (prop === '__isProxy') {
                return true;
            }
            if (prop in target) {
                return target[prop as keyof typeof target];
            }
            return (target[0] as Record<PropertyKey, unknown>)?.[prop];
        },
        set(target, prop, value) {
            if (typeof prop === 'string' && isArrayIndex(prop)) {
                (target as unknown as Record<string, unknown>)[prop] = value;
            } else if (prop in target) {
                (target as unknown as Record<PropertyKey, unknown>)[prop] =
                    value;
            } else {
                (target[0] as Record<PropertyKey, unknown>)[prop] = value;
            }
            return true;
        },
    });
}

/**
 * Reproduces dcmjs's rewrite of SpecificCharacterSet to UTF-8 on read.
 *
 * dcmjs decodes all text into JavaScript strings and re-encodes it as UTF-8
 * when writing, so it replaces the declared character set with 'ISO_IR 192'
 * to keep a dataset self-consistent. That is a real loss — a Latin-1 file
 * saved through dcmjs is transcoded and its declaration rewritten, neither of
 * them announced — and preserving the declaration is one of the reasons this
 * engine exists.
 *
 * The rewrite is nevertheless reproduced here, because a dataset that reaches
 * this shim is still serialized by dcmjs's writer. Handing that writer a
 * dataset declaring ISO_IR 100 whose strings it will encode as UTF-8 produces
 * a file whose declaration contradicts its own bytes — worse than what the
 * application does today. The declaration can only be preserved once the
 * write path is the engine's too.
 */
function naturalizeSpecificCharacterSet(
    element: DicomElement
): unknown[] | undefined {
    return element.tag === SPECIFIC_CHARACTER_SET_TAG
        ? ['ISO_IR 192']
        : undefined;
}

function naturalizeValue(element: DicomElement): unknown[] | undefined {
    const content = element.value;
    switch (content.kind) {
        case 'sequence':
            return content.items.map((item) => naturalizeDataset(item.dataset));
        case 'fragments':
            // dcmjs presents encapsulated pixel data as one buffer per
            // fragment, with the basic offset table dropped.
            return content.value.fragments.map(toArrayBuffer);
        case 'bytes':
            return [toArrayBuffer(content.bytes)];
        case 'bigints':
            return [...content.values];
        case 'numbers':
            return [...content.values];
        case 'strings':
            return naturalizeStrings(element, content.values);
    }
}

/**
 * Renders a string VR's values as dcmjs does.
 *
 * A person name becomes an `{ Alphabetic }` object, and the numeric string
 * VRs become numbers — dcmjs parses DS and IS on read and keeps the text only
 * in `_rawValue`.
 *
 * An attribute present but zero-length takes a different empty form per VR,
 * because dcmjs decodes each family with its own routine: a person name
 * yields no values, a numeric string yields none at all (rendered as null by
 * the caller), and every other string yields one empty string. Callers
 * downstream of this shim were written against those three shapes, so the
 * distinction is reproduced rather than tidied.
 */
function naturalizeStrings(
    element: DicomElement,
    values: readonly string[]
): unknown[] | undefined {
    if (element.vr === 'PN') {
        return values.map((value) => ({ Alphabetic: value }));
    }
    if (element.vr === 'DS' || element.vr === 'IS') {
        if (values.length === 0) {
            return undefined;
        }
        // dcmjs renders a component it cannot parse as null, and call sites
        // behind this projection were written against that. Producing 0 for an
        // empty component instead would hand them a coordinate the file does
        // not contain.
        return values.map((value) => {
            const trimmed = value.trim();
            return trimmed === '' ? null : Number(trimmed);
        });
    }
    if (values.length === 0) {
        return [''];
    }
    return [...values];
}

/**
 * Copies a view of the source buffer into a standalone ArrayBuffer.
 *
 * The engine holds binary values as views to avoid copying; dcmjs's consumers
 * expect an ArrayBuffer whose whole extent is the value, and handing them a
 * view's underlying buffer would expose the entire file. The copy is the
 * price of the shim and disappears with it.
 */
function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
    return bytes.buffer.slice(
        bytes.byteOffset,
        bytes.byteOffset + bytes.byteLength
    ) as ArrayBuffer;
}

/**
 * Builds the `_meta` a naturalized dataset carries: file meta keyed by
 * dictionary keyword, with each attribute left as a `{ vr, Value }` element
 * rather than naturalized.
 *
 * This matches `namifyDataset(dict.meta)`, which is what the application
 * assigns today, and is why `_meta.TransferSyntaxUID.Value[0]` is how callers
 * read the transfer syntax.
 */
function toFileMeta(meta: DicomDataset): DicomJsonDataset {
    const named: DicomJsonDataset = {};
    for (const element of meta.orderedElements()) {
        // dcmjs consumes the group length as framing and does not carry it
        // into the meta it hands back, so neither does this.
        if (element.tag === FILE_META_GROUP_LENGTH_TAG) {
            continue;
        }
        named[naturalNameFor(element.tag)] = {
            vr: element.vr,
            Value: naturalizeValue(element),
        } as DataElement;
    }
    return named;
}

/**
 * The VR an attribute takes when the dictionary is the only source for it.
 *
 * Exposed so a caller assembling a dataset for this projection resolves a VR
 * the same way the reader does.
 */
export function naturalVrForTag(tag: Tag): Vr {
    return vrForTag(tag);
}

/** True when a VR's naturalized values are strings rather than numbers. */
export function naturalizesToStrings(vr: Vr): boolean {
    return isStringVr(vr) && vr !== 'DS' && vr !== 'IS' && vr !== 'PN';
}
